import type { EntityRootData } from '@web-config-app/core';
import { removeHiddenUnicodeCharacters } from '@web-config-app/core';

/**
 * Removes hidden Unicode characters from `entityMetadata.name`.
 *
 * Returns the original reference when no changes are needed (no attributes,
 * no name, or no hidden characters found).
 *
 * Note: `entityMetadata` on `DomainEntityDetailAttributes` is marked
 * `@deprecated` because the codebase is transitioning to
 * `ConfigEntityDetailAttributes` where it is the canonical top-level property.
 * This function already targets that top-level field on the actual data shape.
 */
export const sanitizeEntityMetadataName = (
  entityData: EntityRootData,
): EntityRootData => {
  const { attributes } = entityData;
  if (!attributes) return entityData;

  const { entityMetadata } = attributes;
  if (!entityMetadata?.name) return entityData;

  const sanitizedName = removeHiddenUnicodeCharacters(entityMetadata.name);

  if (sanitizedName === entityMetadata.name) return entityData;

  return {
    ...entityData,
    attributes: {
      ...attributes,
      entityMetadata: {
        ...entityMetadata,
        name: sanitizedName.trim().length < 1 ? undefined : sanitizedName,
      },
    },
  };
};
